<template>
    <transition name="fade">
        <suspense @resolve="isInitialized = true">
            <AppContent>
                <router-view v-slot="{ Component: Region }">
                    <component :is="Region">
                        <router-view v-slot="{ Component, route }">
                            <transition
                                :mode="transitionMode"
                                :name="transitionName"
                            >
                                <suspense
                                    suspensible
                                    @fallback="chunkLoadCompleted"
                                    @pending="chunkLoadStarted"
                                    @resolve="chunkLoadCompleted"
                                >
                                    <component
                                        :is="Component"
                                        :key="
                                            String(
                                                route.meta?.['name'] ||
                                                    route.name
                                            )
                                        "
                                        v-qa.layout="'controller'"
                                    />
                                </suspense>
                            </transition>
                        </router-view>
                    </component>
                </router-view>
            </AppContent>

            <template #fallback>
                <SplashScreen
                    :key="'loader'"
                    class="loader"
                />
            </template>
        </suspense>
    </transition>
</template>

<script lang="ts" setup>
// Line 2: Transition between fallback and default slots of suspense
// Line 3: Root suspense handles init
// Line 5: Root route will always load Region.vue
// Line 8: Transition between routes
// Line 12: Nested suspense handles async views, note the 'suspensible' arg
// Line 18: To disable transition between two given nested routes, use the same meta.name for both
import { provide, ref, watch, onErrorCaptured } from 'vue'
import { storeToRefs } from 'pinia'

import { useRouterTransition } from './composables/app/router-transitions/useRouterTransition'
import { useScrollPositionResolverStore } from './stores/app/lazyLoader'

import AppContent from './AppContent.vue'
import SplashScreen from './components/ui/SplashScreen.vue'
import { useScriptTag } from '@vueuse/core'
import { useFouanalyticsConfig } from '@/plugins/fouanalytics'

const { transitionMode, transitionName } = useRouterTransition()

const { isLazyLoadInProgress, resolveScrollPosition } = storeToRefs(
    useScrollPositionResolverStore()
)

const chunkLoadStarted = () => {
    isLazyLoadInProgress.value = true
}
const chunkLoadCompleted = () => {
    isLazyLoadInProgress.value = false
}

watch(isLazyLoadInProgress, (newVal) => {
    if (!newVal) {
        resolveScrollPosition.value?.()
    }
})

onErrorCaptured((error, target) => {
    // eslint-disable-next-line no-console
    console.log('Captured a Vue.JS Error', error, target)
})

const isInitialized = ref(false)

provide('isInitialized', isInitialized)

const { enabled: fouanalyticsEnabled } = useFouanalyticsConfig()

if (fouanalyticsEnabled) {
    useScriptTag(
        `https://eu.b2c.com/api/init-2047n3vgscwb6i1yph4n.js`,
        () => undefined,
        {
            async: true,
            attrs: {
                'data-cfasync': 'false',
            },
        }
    )
}
</script>

<style
    lang="scss"
    src="@/composables/app/router-transitions/transitions.scss"
></style>

<style lang="scss" scoped>
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.45s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}
</style>
